/**
 * RiftSkillEffect — Astral rift path active: placed spatial tear.
 *
 * Places a gravitational tear at a targeted position. The tear pulls nearby
 * enemies toward its center (via GravityPullSystem) and deals edge damage-
 * over-time to enemies in the outer ring of the pull zone (via ZoneSystem).
 * The tear persists for basePullDurationMs then expires; skill enters cooldown.
 *
 * Evolution: Implosion (implosion) replaces Rift at XP 1200, adding a two-
 * activation model (open tear → collapse early for burst). ImplosionSkillEffect
 * queries resolver with scope 'rift' to inherit all upgrade effects.
 *
 * Upgrade hooks (scope 'rift'):
 * - rift_pull: `pullRadiusTiles` (additive) + `edgeDamagePerSecond` (additive)
 * - rift_duration: `durationMs` (additive, delta)
 *
 * Design values (from design-decisions.md § Astral Base Values):
 * - Pull radius: 2.5 tiles
 * - Pull strength: 1.5 tiles/sec
 * - Edge DoT rate: 2/sec
 * - Well duration: 4000ms
 * - Medium cooldown: 1200ms
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § rift
 * @see design-decisions.md § Astral Base Values
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { GravityPullSystem, GravityWellConfig } from '../../../systems/GravityPullSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RiftDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly gravityPull: Pick<GravityPullSystem, 'addWell' | 'removeWell'>;
  readonly zones: {
    create(config: RiftZoneConfig): string;
    remove(zoneId: string): void;
  };
  readonly targeting: {
    getTargetPosition(): { x: number; y: number };
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
}

/** Minimal zone config shape for Rift's edge DoT zone. */
export interface RiftZoneConfig {
  id: string;
  type: string;
  sourceSkillId: string;
  shape: 'circle';
  position: { x: number; y: number };
  radiusTiles: number;
  durationMs: number;
  dotDefId: string;
  dotIntensity: number;
  edgeRingFraction: number;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface RiftConfig {
  readonly cooldownMs: number;
  readonly basePullRadiusTiles: number;
  readonly basePullStrengthTilesPerSec: number;
  readonly baseWellDurationMs: number;
  readonly baseEdgeDamagePerSecond: number;
  readonly edgeRingFraction: number;
  readonly placementRangeTiles: number;
}

import { TILE_SIZE } from '../../../utils/Constants.js';


const DEFAULT_CONFIG: RiftConfig = {
  cooldownMs: 1200,
  basePullRadiusTiles: 2.5,
  basePullStrengthTilesPerSec: 1.5,
  baseWellDurationMs: 4000,
  baseEdgeDamagePerSecond: 2,
  edgeRingFraction: 0.3,
  placementRangeTiles: 6,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RiftSkillEffect implements SkillEffect {
  readonly skillId: string = 'rift';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: RiftDeps;
  readonly config: RiftConfig;

  private _cooldownRemaining = 0;
  private _wellTimer = 0;           // counts down active well duration
  private _wellId: string | null = null;
  private _zoneId: string | null = null;
  private _activationsCount = 0;
  private _resolvedCooldownMs = 0;
  private _resolvedDurationMs = 0;
  private _resolvedRadiusTiles = 0;
  private _resolvedEdgeDps = 0;
  /** Clamped placement position of the most recently opened well. */
  protected _lastPlacedPosition: { x: number; y: number } | null = null;

  constructor(deps: RiftDeps, config: Partial<RiftConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get activationsCount(): number { return this._activationsCount; }
  get resolvedCooldownMs(): number { return this._resolvedCooldownMs; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get resolvedRadiusTiles(): number { return this._resolvedRadiusTiles; }
  get resolvedEdgeDps(): number { return this._resolvedEdgeDps; }
  get isWellActive(): boolean { return this._wellTimer > 0; }
  get activeWellId(): string | null { return this._wellId; }
  get activeZoneId(): string | null { return this._zoneId; }
  get cooldown(): number { return this._cooldownRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this.isWellActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve duration: base + rift_duration additive bonus.
    const durationBonus = this.deps.resolver.getAdditiveEffect('rift', 'durationMs');
    this._resolvedDurationMs = this.config.baseWellDurationMs + durationBonus;

    // Resolve pull radius: base + rift_pull additive bonus.
    const radiusBonus = this.deps.resolver.getAdditiveEffect('rift', 'pullRadiusTiles');
    this._resolvedRadiusTiles = this.config.basePullRadiusTiles + radiusBonus;

    // Resolve edge damage: base + rift_pull additive bonus.
    const edgeBonus = this.deps.resolver.getAdditiveEffect('rift', 'edgeDamagePerSecond');
    this._resolvedEdgeDps = this.config.baseEdgeDamagePerSecond + edgeBonus;

    // Cooldown (not modified by resolver — pure tier).
    this._resolvedCooldownMs = this.config.cooldownMs;

    // Clamp target to placement range.
    const from = this.deps.player.getPosition();
    const target = this.deps.targeting.getTargetPosition();
    const dx = target.x - from.x;
    const dy = target.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxRangePx = this.config.placementRangeTiles * TILE_SIZE;
    let tx: number;
    let ty: number;
    if (dist <= 0.001 || dist <= maxRangePx) {
      tx = target.x;
      ty = target.y;
    } else {
      tx = from.x + (dx / dist) * maxRangePx;
      ty = from.y + (dy / dist) * maxRangePx;
    }

    const position = { x: tx, y: ty };
    this._lastPlacedPosition = { ...position };

    // Register gravity well.
    const wellConfig: GravityWellConfig = {
      id: `rift_well_${this._activationsCount}`,
      position,
      pullRadiusTiles: this._resolvedRadiusTiles,
      pullStrengthTilesPerSec: this.config.basePullStrengthTilesPerSec,
      durationMs: this._resolvedDurationMs,
      sourceSkillId: 'rift',
    };
    this._wellId = this.deps.gravityPull.addWell(wellConfig);

    // Register edge DoT zone.
    const zoneConfig: RiftZoneConfig = {
      id: `rift_zone_${this._activationsCount}`,
      type: 'rift_edge_dot',
      sourceSkillId: 'rift',
      shape: 'circle',
      position,
      radiusTiles: this._resolvedRadiusTiles,
      durationMs: this._resolvedDurationMs,
      dotDefId: 'spatial_edge_damage',
      dotIntensity: this._resolvedEdgeDps,
      edgeRingFraction: this.config.edgeRingFraction,
    };
    this._zoneId = this.deps.zones.create(zoneConfig);

    // Start the active well timer.
    this._wellTimer = this._resolvedDurationMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Rift is an instant activate — deactivate is unused.
  }

  isActive(): boolean {
    return this._wellTimer > 0;
  }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick well lifetime. When it expires, clean up and start cooldown.
    if (this._wellTimer > 0) {
      this._wellTimer = Math.max(0, this._wellTimer - delta);
      if (this._wellTimer <= 0) {
        this._expireWell();
      }
    }
  }

  protected _expireWell(): void {
    if (this._wellId !== null) {
      this.deps.gravityPull.removeWell(this._wellId);
      this._wellId = null;
    }
    if (this._zoneId !== null) {
      this.deps.zones.remove(this._zoneId);
      this._zoneId = null;
    }
    this._wellTimer = 0;
    this._cooldownRemaining = this._resolvedCooldownMs;
  }

  destroy(): void {
    if (this._wellTimer > 0) {
      this._expireWell();
    }
    this._cooldownRemaining = 0;
  }
}
